import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { MapData } from '../types';

interface MapProps {
  data: MapData;
  onMapRef?: (ref: HTMLDivElement | null) => void;
}

export default function Map({ data, onMapRef }: MapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [baseImageLoaded, setBaseImageLoaded] = useState(false);
  const [layerImagesLoaded, setLayerImagesLoaded] = useState<Record<string, boolean>>({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (onMapRef && mapRef.current) {
      onMapRef(mapRef.current);
    }
  }, [onMapRef]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Verificar inicialmente
    checkMobile();
    
    // Adicionar listener para quando a janela for redimensionada
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleBaseImageLoad = () => {
    setBaseImageLoaded(true);
  };

  const handleLayerImageLoad = (layerId: string) => {
    setLayerImagesLoaded(prev => ({ ...prev, [layerId]: true }));
  };

  return (
    <motion.div
      ref={mapRef}
      id={`map-${data.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full rounded-lg overflow-hidden shadow-lg relative bg-gray-100 p-0 m-0 h-full"
      style={{ 
        aspectRatio: isMobile ? '1/1' : '4/3', 
        position: 'relative', 
        display: 'block',
        minHeight: '240px',
        height: 'calc(100vh - 320px)',
        maxHeight: '600px',
        flexGrow: 1,
        overflowY: 'hidden',
        overflowX: 'hidden'
      }}
    >
      {data.baseImageUrl && (
        <img
          src={data.baseImageUrl}
          alt="Mapa base"
          className="absolute inset-0 w-full h-full object-contain sm:object-fill bg-white p-0 m-0"
          style={{ 
            visibility: baseImageLoaded ? 'visible' : 'hidden'
          }}
          onLoad={handleBaseImageLoad}
          onError={(e) => console.error(`Error loading base image: ${data.baseImageUrl}`, e)}
        />
      )}
      <AnimatePresence>
        {data.layers
          .filter(layer => layer.isActive && layer.imageUrl)
          .sort((a, b) => {
            // Special case for area map: danger-zones should be above bike-parking
            if (data.id === 'area') {
              if (a.id === 'danger-zones' && b.id === 'bike-parking') return 1;
              if (a.id === 'bike-parking' && b.id === 'danger-zones') return -1;
            }
            
            // Check if layer is in "Zonas a Destacar" section
            const isAZonasDestaque = a.section === 'Zonas a Destacar';
            const isBZonasDestaque = b.section === 'Zonas a Destacar';
            
            // Put "Zonas a Destacar" layers at the bottom
            if (isAZonasDestaque && !isBZonasDestaque) return -1;
            if (!isAZonasDestaque && isBZonasDestaque) return 1;
            return 0;
          })
          .map((layer, index) => (
            <motion.img
              key={layer.id}
              src={layer.imageUrl}
              alt={layer.name}
              className="absolute inset-0 w-full h-full object-contain sm:object-fill p-0 m-0"
              style={{ 
                visibility: layerImagesLoaded[layer.id] ? 'visible' : 'hidden',
                zIndex: index + 10,
                opacity: layer.opacity || 1
              }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: layerImagesLoaded[layer.id] ? (layer.opacity || 1) : 0
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "linear" }}
              onLoad={() => handleLayerImageLoad(layer.id)}
              onError={(e) => console.error(`Error loading layer image: ${layer.imageUrl}`, e)}
            />
          ))}
      </AnimatePresence>
    </motion.div>
  );
}